import type { GetSimulationResponse, PriceUnitDto } from '@/api';
import { Permission } from '@/api';
import PermissionWrapper from '@/components/auth/permission-wrapper';
import { FilterStatus } from '@/components/table/toolbar/filter-status';
import { VerticalSeparator } from '@/components/table/toolbar/vertical-separator';
import { themeColors } from '@/theme/colors';
import { Button } from '@lego/connect-components-react';
import { Flex } from '@lego/klik-ui';
import { ThemeProvider, createTheme } from '@mui/material';
import {
  type MRT_ExpandedState,
  MRT_GlobalFilterTextField,
  MRT_ShowHideColumnsButton,
  type MRT_SortingState,
  MRT_ToggleFiltersButton,
  MRT_ToggleFullScreenButton,
  MaterialReactTable,
} from 'material-react-table';
import type { RefObject } from 'react';
import { memo, useEffect, useMemo, useState } from 'react';
import {
  HEIGHT_OFFSET,
  type SimulationView,
} from '../../simulation-view-constants';
import type { PricelistSettings, RowData } from '../../utils/simulation-types';
import { SIM_MODAL } from '../../utils/simulation-types';
import SimulationViewDropdown from '../toolbar/simulation-view-dropdown';
import AlternativePriceListSelector from './alternative-pricelist-selector';
import {
  EXPAND_COLUMN_WIDTH,
  getHarmonizationColumnPinning,
  getHarmonizationColumns,
  getHarmonizationGrouping,
  getHarmonizationSorting,
} from './harmonization-column-config';
import {
  formatHarmonizationDataForDisplay,
  transformToHarmonizationPivot,
} from './harmonization-pivot-transformer';

const EMPTY_PRICE_UNITS: PriceUnitDto[] = [];

type HarmonizationPivotTableProps = {
  data: Map<string, RowData>;
  simulationDetails: GetSimulationResponse;
  pricelistSettings: Map<string, PricelistSettings>;
  selectedView: SimulationView;
  isReadOnly: boolean;
  publishTriggerRef: RefObject<HTMLButtonElement | null>;
  setSelectedView: (view: SimulationView) => void;
  setShowModal: (modal: SIM_MODAL | null) => void;
  isPublishDisabled: boolean;
};

const MUI_THEME = createTheme({
  palette: {
    mode: 'light',
  },
  components: {
    MuiBadge: {
      styleOverrides: {
        badge: {
          display: 'none',
        },
      },
    },
  },
});

const HarmonizationPivotTable = memo(
  ({
    data,
    simulationDetails,
    pricelistSettings,
    selectedView,
    isReadOnly,
    publishTriggerRef,
    setSelectedView,
    setShowModal,
    isPublishDisabled,
  }: HarmonizationPivotTableProps) => {
    const priceLists =
      simulationDetails.planningPriceUnits ?? EMPTY_PRICE_UNITS;

    const sortedPriceLists = useMemo(
      () =>
        [...priceLists].sort((a, b) =>
          (a.name || '').localeCompare(b.name || ''),
        ),
      [priceLists],
    );

    const [alternativePriceListId, setAlternativePriceListId] = useState<
      number | null
    >(null);

    useEffect(() => {
      if (alternativePriceListId === null && priceLists.length > 0) {
        setAlternativePriceListId(priceLists[0]?.id ?? null);
      }
    }, [priceLists, alternativePriceListId]);

    const pivotData = useMemo(
      () =>
        alternativePriceListId !== null
          ? transformToHarmonizationPivot(
              Array.from(data.values()),
              priceLists,
              alternativePriceListId,
              pricelistSettings,
            )
          : [],
      [data, priceLists, alternativePriceListId, pricelistSettings],
    );

    const columns = useMemo(
      () =>
        alternativePriceListId !== null
          ? getHarmonizationColumns(priceLists, alternativePriceListId)
          : [],
      [priceLists, alternativePriceListId],
    );

    const formattedData = useMemo(
      () => formatHarmonizationDataForDisplay(pivotData),
      [pivotData],
    );

    const grouping = useMemo(() => getHarmonizationGrouping(), []);

    const columnPinning = useMemo(
      () =>
        alternativePriceListId !== null
          ? getHarmonizationColumnPinning(alternativePriceListId)
          : { left: [] },
      [alternativePriceListId],
    );

    const [sorting, setSorting] = useState<MRT_SortingState>(() =>
      getHarmonizationSorting(),
    );

    const [expanded, setExpanded] = useState<MRT_ExpandedState>({});

    useEffect(() => {
      if (pivotData.length === 0) return;

      const uniqueThemes = new Set(pivotData.map((row) => row.themeName));

      const firstLevelExpanded: Record<string, boolean> = {};
      for (const value of uniqueThemes) {
        firstLevelExpanded[`themeName:${value}`] = true;
      }
      setExpanded(firstLevelExpanded);
    }, [pivotData]);

    if (alternativePriceListId === null) return null;

    return (
      <div style={{ width: '100%', position: 'relative' }}>
        <ThemeProvider theme={MUI_THEME}>
          <MaterialReactTable
            columns={columns}
            data={formattedData}
            getRowId={(row) => String(row.productId)}
            enableGrouping
            groupedColumnMode={false}
            state={{
              grouping,
              sorting,
              expanded,
              columnPinning,
            }}
            onSortingChange={setSorting}
            onExpandedChange={setExpanded}
            enableRowSelection={false}
            enableEditing={false}
            enablePagination={false}
            enableRowVirtualization
            enableStickyHeader
            enableFacetedValues
            layoutMode="grid"
            enableColumnResizing
            enableColumnPinning
            positionExpandColumn="first"
            displayColumnDefOptions={{
              'mrt-row-expand': {
                size: EXPAND_COLUMN_WIDTH,
              },
            }}
            muiTableBodyRowProps={({ table }) => ({
              sx: {
                height:
                  table.getState().density === 'compact'
                    ? '30px'
                    : table.getState().density === 'comfortable'
                      ? '50px'
                      : '70px',
              },
            })}
            muiTableContainerProps={({ table }) => ({
              sx: {
                height: table.getState().isFullScreen
                  ? 'calc(100vh - 100px)'
                  : `calc(100vh - ${HEIGHT_OFFSET}px)`,
                overflow: 'auto',
                zIndex: 0,
              },
            })}
            renderTopToolbar={({ table }) => (
              <Flex
                padding="8px 16px"
                backgroundColor={themeColors.surface.background.light}
                justifyContent="space-between"
                gap="16px"
                alignItems="center"
              >
                <Flex gap="12px" alignItems="center" flex={1}>
                  <MRT_GlobalFilterTextField
                    table={table}
                    placeholder="Search"
                  />
                  <MRT_ToggleFiltersButton table={table} />
                  <MRT_ShowHideColumnsButton table={table} />
                  <MRT_ToggleFullScreenButton table={table} />
                  <VerticalSeparator />
                  <SimulationViewDropdown
                    selectedView={selectedView}
                    setSelectedView={setSelectedView}
                    userGroupView="core"
                    setUserGroupView={() => {}}
                  />
                  <VerticalSeparator />
                  <FilterStatus table={table} />
                </Flex>
                <Flex gap="8px" alignItems="center">
                  <AlternativePriceListSelector
                    priceLists={sortedPriceLists}
                    selectedPriceListId={alternativePriceListId}
                    onSelect={(priceListId) => {
                      if (priceListId !== alternativePriceListId) {
                        table.resetColumnFilters();
                        table.resetGlobalFilter();
                      }
                      setAlternativePriceListId(priceListId);
                    }}
                  />
                  <VerticalSeparator />
                  <PermissionWrapper
                    requiredPermissions={[Permission.WriteSimulations]}
                  >
                    <Button
                      aria-label="Publish prices"
                      variant="primary"
                      appearance="solid"
                      size="small"
                      disabled={isPublishDisabled || isReadOnly}
                      onClick={() => setShowModal(SIM_MODAL.Publish)}
                      ref={publishTriggerRef}
                    >
                      Publish prices
                    </Button>
                  </PermissionWrapper>
                </Flex>
              </Flex>
            )}
            renderBottomToolbar={({ table }) => (
              <Flex my={1} mx={2} fontSize="11px">
                {table.getFilteredRowModel().rows.length.toLocaleString()} rows
              </Flex>
            )}
            muiTableBodyCellProps={({ row }) => ({
              sx: {
                ...(row.getIsGrouped() && {
                  fontWeight: 'bold',
                }),
              },
            })}
            initialState={{
              density: 'compact',
              showGlobalFilter: true,
              showColumnFilters: true,
            }}
            enableGlobalFilter
            enableColumnActions={false}
            enableKeyboardShortcuts
          />
        </ThemeProvider>
      </div>
    );
  },
);

HarmonizationPivotTable.displayName = 'HarmonizationPivotTable';

export default HarmonizationPivotTable;
